/**
 * useTelegram — хук для Telegram WebApp SDK
 *
 * Предоставляет доступ к:
 * - tg.initData (для авторизации на сервере)
 * - tg.themeParams (цвета темы)
 * - BackButton, MainButton, HapticFeedback
 * - User info
 */

import { useCallback, useEffect, useMemo } from 'react';

// ─── Types ─────────────────────────────────────────────────

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
}

interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface WebApp {
  initData: string;
  initDataUnsafe: {
    user?: TelegramUser;
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  ready: () => void;
  expand: () => void;
  close: () => void;
  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  MainButton: {
    text: string;
    isVisible: boolean;
    isActive: boolean;
    show: () => void;
    hide: () => void;
    setText: (text: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  showAlert: (message: string) => void;
  showConfirm: (message: string, callback: (confirmed: boolean) => void) => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: WebApp;
    };
  }
}

// ─── Hook ──────────────────────────────────────────────────

export function useTelegram() {
  const tg = window.Telegram?.WebApp;

  const user = useMemo((): TelegramUser | null => {
    return tg?.initDataUnsafe?.user ?? null;
  }, [tg]);

  const isDarkMode = useMemo(() => {
    return tg?.colorScheme === 'dark';
  }, [tg]);

  const initData = useMemo(() => {
    return tg?.initData ?? '';
  }, [tg]);

  // Telegram ready + expand при монтировании
  useEffect(() => {
    if (tg) {
      tg.ready();
      tg.expand();
    }
  }, [tg]);

  const showBackButton = useCallback((onBack: () => void) => {
    if (!tg) return;
    tg.BackButton.show();
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton.hide();
      tg.BackButton.offClick(onBack);
    };
  }, [tg]);

  const haptic = useMemo(() => ({
    impact: (style: 'light' | 'medium' | 'heavy' = 'light') => {
      tg?.HapticFeedback?.impactOccurred(style);
    },
    success: () => tg?.HapticFeedback?.notificationOccurred('success'),
    error: () => tg?.HapticFeedback?.notificationOccurred('error'),
    selection: () => tg?.HapticFeedback?.selectionChanged(),
  }), [tg]);

  return {
    tg,
    user,
    isDarkMode,
    initData,
    showBackButton,
    haptic,
    isInTelegram: !!tg,
  };
}
